/**
 * Deciding what a failed profile call actually means.
 *
 * The app was treating any `/auth/me` failure as "not signed in" and rendering the
 * login screen. That conflates two different questions: *who are you* (the Entra
 * token) and *what may you do* (the backend profile). A 5xx or a dropped
 * connection answers neither — and sending the user to a login screen there is
 * useless, because signing in again cannot fix an outage and the redirect lands
 * back on the same failure.
 */

/** Backoff before re-asking for the profile. A cold backend usually answers by
 *  the second or third try, so most blips never reach the user. */
export const ME_RETRY_DELAYS_MS = [400, 1200];

export type SessionLoadResult<T> =
  | { status: 'ok'; session: T }
  | { status: 'signed-out' }
  | { status: 'unreachable' };

/** True only when the credentials themselves were refused. */
export function isCredentialRejection(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
}

/**
 * Load the session, retrying transient failures and classifying the outcome.
 *
 * A credential rejection short-circuits immediately: retrying a 401 just delays
 * the login the user actually needs.
 */
export async function loadSessionWithRetry<T>(
  load: () => Promise<T>,
  options: {
    delays?: number[];
    sleep?: (ms: number) => Promise<void>;
  } = {},
): Promise<SessionLoadResult<T>> {
  const delays = options.delays ?? ME_RETRY_DELAYS_MS;
  const sleep = options.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

  for (let attempt = 0; ; attempt++) {
    try {
      return { status: 'ok', session: await load() };
    } catch (error) {
      if (isCredentialRejection(error)) return { status: 'signed-out' };
      if (attempt < delays.length) {
        await sleep(delays[attempt]);
        continue;
      }
      return { status: 'unreachable' };
    }
  }
}
